// src/utils/educationUtils.js
// 🎓 教育重視人員配置システム - ユーティリティ関数集

import { skillScoreMap } from "../constants/skillLevels";
import dayjs from "dayjs";

// 🎯 教育タイプの定義
export const EDUCATION_TYPES = {
  FIRST_OBSERVATION: "first_observation",      // 初回見学
  GUIDED_PRACTICE: "guided_practice",          // 指導下実践
  SUPERVISED_INDEPENDENCE: "supervised_independence", // 監督下独立
  FINAL_ASSESSMENT: "final_assessment",        // 最終評価
  ADDITIONAL_OBSERVATION: "additional_observation", // 追加見学
  SURGICAL_ASSISTANCE: "surgical_assistance",  // 手術補助
  SKILL_DEVELOPMENT: "skill_development",      // スキル開発
  ADDITIONAL_TEACHING: "additional_teaching"   // 追加指導
};

// 🎯 教育優先度の定義
export const EDUCATION_PRIORITIES = {
  HIGH: "high",
  MEDIUM: "medium", 
  LOW: "low"
};

// 🎯 スキルレベル閾値
export const SKILL_THRESHOLDS = {
  BEGINNER_MAX: 3,      // 初心者の最大レベル
  INTERMEDIATE_MAX: 5,  // 中級者の最大レベル
  ADVANCED_MIN: 6,      // 上級者の最小レベル
  INSTRUCTOR_MIN: 7     // 指導者の最小レベル
};

/**
 * 🎓 個別スタッフの教育ニーズを詳細分析
 * @param {Object} staff - スタッフ情報
 * @param {Object} surgery - 手術情報
 * @param {string} role - 役割（scrub/circulating）
 * @returns {Object} 教育ニーズ分析結果
 */
export const analyzeIndividualEducationNeed = async (staff, surgery, role) => {
  const skillData = staff.skills || {};
  const experienceYears = staff.experienceYears || 0;
  
  // 基本情報
  const department = surgery.department || "一般外科";
  const procedure = surgery.procedure || "";
  
  // 現在のスキルレベル詳細分析
  const currentSkillAnalysis = analyzeCurrentSkillLevel(skillData, department, role, procedure);
  
  // 目標スキルレベルの設定
  const targetSkillLevel = determineTargetSkillLevel(surgery, role, experienceYears);
  
  // 教育の必要性と緊急度
  const educationUrgency = calculateEducationUrgency(
    currentSkillAnalysis.level,
    targetSkillLevel,
    experienceYears,
    surgery.priority
  );
  
  if (!educationUrgency.needsEducation) {
    return { needsEducation: false };
  }
  
  // 詳細な教育計画
  const educationPlan = createDetailedEducationPlan(
    currentSkillAnalysis,
    targetSkillLevel,
    surgery,
    role,
    experienceYears
  );
  
  return {
    needsEducation: true,
    currentLevel: currentSkillAnalysis.level,
    currentSkillDetails: currentSkillAnalysis,
    targetLevel: targetSkillLevel,
    educationType: educationPlan.type,
    priority: educationUrgency.priority,
    urgencyScore: educationUrgency.score,
    skillGap: targetSkillLevel - currentSkillAnalysis.level,
    educationPlan: educationPlan,
    expectedOutcome: educationPlan.expectedOutcome,
    timeRequirement: educationPlan.timeRequirement
  };
};

/**
 * 🔍 現在スキルレベルの詳細分析
 */
export const analyzeCurrentSkillLevel = (skillData, department, role, procedure) => {
  if (!skillData || !skillData.departments) {
    return {
      level: 0,
      confidence: "low",
      gaps: ["基本スキル習得", "診療科理解", "手術流れ把握"],
      strengths: []
    };
  }
  
  const deptSkills = skillData.departments[department];
  if (!deptSkills) {
    return {
      level: 0,
      confidence: "low",
      gaps: [`${department}未経験`, "診療科特有技術", "専門器械"],
      strengths: []
    };
  }
  
  const skillLevel = role === "scrub" 
    ? (deptSkills.scrubScore || 0) 
    : (deptSkills.circulatingScore || 0);
  
  // 詳細ギャップ分析
  const gaps = identifyDetailedSkillGaps(skillLevel, department, role, procedure);
  const strengths = identifySkillStrengths(deptSkills, role);
  
  return {
    level: skillLevel,
    confidence: skillLevel >= 4 ? "high" : skillLevel >= 2 ? "medium" : "low",
    gaps,
    strengths
  };
};

/**
 * 🎯 目標スキルレベルの決定
 */
export const determineTargetSkillLevel = (surgery, role, experienceYears) => {
  const difficulty = surgery.difficulty || "normal";
  const procedure = surgery.procedure || "";
  
  // 基本目標レベル
  let baseLevel = SKILL_THRESHOLDS.ADVANCED_MIN; // 独り立ち
  
  // 難易度による調整
  if (difficulty === "high" || isComplexProcedure(procedure)) {
    baseLevel = SKILL_THRESHOLDS.INTERMEDIATE_MAX; // 外から見守り（高難度では慎重に）
  } else if (difficulty === "low" || isSimpleProcedure(procedure)) {
    baseLevel = SKILL_THRESHOLDS.ADVANCED_MIN; // 独り立ち
  }
  
  // 役割による微調整
  if (role === "scrub" && isInstrumentIntensiveProcedure(procedure)) {
    baseLevel = Math.max(baseLevel, SKILL_THRESHOLDS.INTERMEDIATE_MAX); // 器械出しは特に重要
  }
  
  // 経験年数による調整
  if (experienceYears <= 1) {
    baseLevel = Math.min(baseLevel, 4); // 1年目は経験3回以上まで
  } else if (experienceYears <= 2) {
    baseLevel = Math.min(baseLevel, SKILL_THRESHOLDS.INTERMEDIATE_MAX); // 2年目は外から見守りまで
  }
  
  return baseLevel;
};

/**
 * 🚨 教育緊急度の計算
 */
export const calculateEducationUrgency = (currentLevel, targetLevel, experienceYears, surgeryPriority) => {
  const skillGap = targetLevel - currentLevel;
  
  if (skillGap <= 0) {
    return { needsEducation: false, priority: EDUCATION_PRIORITIES.LOW, score: 0 };
  }
  
  let urgencyScore = skillGap * 20; // ベーススコア
  let priority = EDUCATION_PRIORITIES.MEDIUM;
  
  // 1年目は高優先度
  if (experienceYears <= 1) {
    urgencyScore += 30;
    priority = EDUCATION_PRIORITIES.HIGH;
  }
  
  // 大きなスキルギャップは高優先度
  if (skillGap >= 3) {
    urgencyScore += 25;
    priority = EDUCATION_PRIORITIES.HIGH;
  }
  
  // 緊急手術の場合は低優先度（安全性重視）
  if (surgeryPriority === "emergency") {
    urgencyScore -= 20;
    priority = EDUCATION_PRIORITIES.LOW;
  }
  
  // 独り立ち直前は高優先度
  if (currentLevel === SKILL_THRESHOLDS.INTERMEDIATE_MAX && targetLevel === SKILL_THRESHOLDS.ADVANCED_MIN) {
    urgencyScore += 30;
    priority = EDUCATION_PRIORITIES.HIGH;
  }
  
  return {
    needsEducation: true,
    priority,
    score: Math.max(0, Math.min(100, urgencyScore))
  };
};

/**
 * 📋 詳細教育計画の作成
 */
export const createDetailedEducationPlan = (currentSkillAnalysis, targetLevel, surgery, role, experienceYears) => {
  const currentLevel = currentSkillAnalysis.level;
  const skillGap = targetLevel - currentLevel;
  
  let educationType = EDUCATION_TYPES.GUIDED_PRACTICE;
  let timeRequirement = 120; // デフォルト2時間
  let expectedOutcome = "";
  
  // 教育タイプの決定
  if (currentLevel === 0) {
    educationType = EDUCATION_TYPES.FIRST_OBSERVATION;
    timeRequirement = 60;
    expectedOutcome = "基本的な手術の流れと役割の理解";
  } else if (currentLevel <= 2) {
    educationType = EDUCATION_TYPES.GUIDED_PRACTICE;
    timeRequirement = 180;
    expectedOutcome = "指導下での実践経験と手技習得";
  } else if (currentLevel <= 4) {
    educationType = EDUCATION_TYPES.SUPERVISED_INDEPENDENCE;
    timeRequirement = 150;
    expectedOutcome = "監督下での独立実践と自信向上";
  } else if (currentLevel === SKILL_THRESHOLDS.INTERMEDIATE_MAX) {
    educationType = EDUCATION_TYPES.FINAL_ASSESSMENT;
    timeRequirement = 180;
    expectedOutcome = "独り立ち可否の最終判定";
  } else {
    educationType = EDUCATION_TYPES.SKILL_DEVELOPMENT;
    timeRequirement = 90;
    expectedOutcome = "より高度な技術の習得";
  }
  
  return {
    type: educationType,
    timeRequirement,
    expectedOutcome,
    skillGap,
    educationSteps: generateEducationSteps(currentLevel, targetLevel, role),
    evaluationCriteria: generateEvaluationCriteria(targetLevel, role)
  };
};

/**
 * 📝 教育ステップの生成
 */
export const generateEducationSteps = (currentLevel, targetLevel, role) => {
  const steps = [];
  
  for (let level = currentLevel + 1; level <= targetLevel; level++) {
    const stepDescription = getSkillLevelDescription(level, role);
    steps.push({
      level,
      description: stepDescription,
      estimatedTime: getEstimatedTimeForLevel(level),
      prerequisites: getPrerequisitesForLevel(level)
    });
  }
  
  return steps;
};

/**
 * 📊 評価基準の生成
 */
export const generateEvaluationCriteria = (targetLevel, role) => {
  const criteria = [];
  
  if (role === "scrub") {
    criteria.push("器械の先読み能力");
    criteria.push("術野の理解度");
    criteria.push("手術流れの把握");
    
    if (targetLevel >= SKILL_THRESHOLDS.ADVANCED_MIN) {
      criteria.push("独立した判断能力");
      criteria.push("緊急時対応");
    }
  } else if (role === "circulating") {
    criteria.push("チーム調整能力");
    criteria.push("記録業務の正確性");
    criteria.push("安全管理意識");
    
    if (targetLevel >= SKILL_THRESHOLDS.ADVANCED_MIN) {
      criteria.push("効率的な業務進行");
      criteria.push("問題解決能力");
    }
  }
  
  return criteria;
};

/**
 * 🔍 詳細スキルギャップの特定
 */
export const identifyDetailedSkillGaps = (currentLevel, department, role, procedure) => {
  const gaps = [];
  
  if (currentLevel < 2) {
    gaps.push("基本的な手術流れの理解", "基本器械の名称と用途");
  }
  
  if (currentLevel < 4) {
    gaps.push(`${department}特有の手技`, "緊急時対応");
  }
  
  if (currentLevel < SKILL_THRESHOLDS.ADVANCED_MIN) {
    gaps.push("独立した判断能力", "効率的な業務進行");
  }
  
  // 手術特有のギャップ
  if (isComplexProcedure(procedure)) {
    gaps.push("高難度手術対応", "特殊器械操作");
  }
  
  if (role === "scrub") {
    if (currentLevel < SKILL_THRESHOLDS.INTERMEDIATE_MAX) {
      gaps.push("器械の先読み", "術野の理解");
    }
  } else if (role === "circulating") {
    if (currentLevel < SKILL_THRESHOLDS.INTERMEDIATE_MAX) {
      gaps.push("チーム調整", "記録業務");
    }
  }
  
  return gaps;
};

/**
 * ✨ スキルの強みを特定
 */
export const identifySkillStrengths = (deptSkills, role) => {
  const strengths = [];
  
  const averageScore = deptSkills.averageScore || 0;
  const roleScore = role === "scrub" ? deptSkills.scrubScore : deptSkills.circulatingScore;
  
  if (averageScore >= SKILL_THRESHOLDS.ADVANCED_MIN) {
    strengths.push("この診療科での豊富な経験");
  }
  
  if (roleScore >= SKILL_THRESHOLDS.INTERMEDIATE_MAX) {
    strengths.push(`${role === "scrub" ? "器械出し" : "外回り"}での安定した実績`);
  }
  
  if (deptSkills.scrubScore >= 4 && deptSkills.circulatingScore >= 4) {
    strengths.push("両役割での幅広い経験");
  }
  
  return strengths;
};

/**
 * 🏥 手術特性の判定関数群
 */
export const isComplexProcedure = (procedure) => {
  const complexKeywords = [
    "開心術", "脳腫瘍", "肝切除", "膵頭十二指腸切除", 
    "食道切除", "血管再建", "移植"
  ];
  return complexKeywords.some(keyword => procedure.includes(keyword));
};

export const isSimpleProcedure = (procedure) => {
  const simpleKeywords = [
    "ヘルニア", "痔核", "皮膚腫瘍", "生検", "抜釘"
  ];
  return simpleKeywords.some(keyword => procedure.includes(keyword));
};

export const isInstrumentIntensiveProcedure = (procedure) => {
  const instrumentKeywords = [
    "腹腔鏡", "関節鏡", "内視鏡", "マイクロ", "ロボット支援"
  ];
  return instrumentKeywords.some(keyword => procedure.includes(keyword));
};

export const isRareProcedure = (procedure) => {
  const rareKeywords = [
    "移植", "血管再建", "顕微鏡", "ロボット支援", 
    "内視鏡的粘膜下剥離", "経皮的", "3D"
  ];
  return rareKeywords.some(keyword => procedure.includes(keyword));
};

export const isInnovativeProcedure = (procedure) => {
  const innovativeKeywords = [
    "ロボット", "AI支援", "3D", "VR", "AR", 
    "ナビゲーション", "最新", "新技術"
  ];
  return innovativeKeywords.some(keyword => procedure.includes(keyword));
};

/**
 * 📈 指導能力の計算
 */
export const calculateInstructionCapability = (staff) => {
  let score = 0;
  const specialties = [];
  
  const experienceYears = staff.experienceYears || 0;
  const skillScore = staff.skillScore || 0;
  
  // 基本条件チェック
  if (experienceYears < 3 || skillScore < 4.0) {
    return { canInstruct: false, score: 0, specialties: [] };
  }
  
  // 指導能力スコア計算
  score += Math.min(40, experienceYears * 4);
  score += Math.min(30, (skillScore - 4) * 15);
  
  // 専門分野の特定
  if (staff.skills && staff.skills.departments) {
    Object.entries(staff.skills.departments).forEach(([dept, skills]) => {
      const avgScore = ((skills.scrubScore || 0) + (skills.circulatingScore || 0)) / 2;
      if (avgScore >= SKILL_THRESHOLDS.ADVANCED_MIN) {
        specialties.push({
          department: dept,
          level: avgScore >= SKILL_THRESHOLDS.INSTRUCTOR_MIN ? "expert" : "advanced",
          canTeachScrub: (skills.scrubScore || 0) >= SKILL_THRESHOLDS.ADVANCED_MIN,
          canTeachCirculating: (skills.circulatingScore || 0) >= SKILL_THRESHOLDS.ADVANCED_MIN
        });
      }
    });
  }
  
  // 専門分野がある場合の加点
  score += specialties.length * 10;
  
  return {
    canInstruct: score >= 50,
    score,
    specialties,
    maxSimultaneousStudents: Math.floor(score / 25),
    preferredEducationTypes: determinePreferredEducationTypes(experienceYears, skillScore)
  };
};

/**
 * 🎓 学習ポテンシャル計算
 */
export const calculateLearningPotential = (staff, educationNeeds) => {
  let score = 50; // ベース
  const priorityAreas = [];
  
  const experienceYears = staff.experienceYears || 0;
  const skillScore = staff.skillScore || 0;
  
  // 学習適性
  if (experienceYears <= 3) {
    score += 30; // 若手は学習優先度高
  } else if (experienceYears <= 6) {
    score += 15; // 中堅も学習価値あり
  }
  
  if (skillScore < 5.0) {
    score += 20; // スキル向上の余地大
  }
  
  // 学習優先エリアの特定
  if (staff.skills && staff.skills.departments) {
    Object.entries(staff.skills.departments).forEach(([dept, skills]) => {
      const scrubScore = skills.scrubScore || 0;
      const circulatingScore = skills.circulatingScore || 0;
      
      if (scrubScore < SKILL_THRESHOLDS.ADVANCED_MIN) {
        priorityAreas.push({
          area: `${dept}_scrub`,
          currentLevel: scrubScore,
          targetLevel: SKILL_THRESHOLDS.ADVANCED_MIN,
          urgency: scrubScore < 4 ? EDUCATION_PRIORITIES.HIGH : EDUCATION_PRIORITIES.MEDIUM
        });
      }
      
      if (circulatingScore < SKILL_THRESHOLDS.ADVANCED_MIN) {
        priorityAreas.push({
          area: `${dept}_circulating`,
          currentLevel: circulatingScore,
          targetLevel: SKILL_THRESHOLDS.ADVANCED_MIN,
          urgency: circulatingScore < 4 ? EDUCATION_PRIORITIES.HIGH : EDUCATION_PRIORITIES.MEDIUM
        });
      }
    });
  } else {
    // スキルデータがない場合は基本学習が必要
    priorityAreas.push({
      area: "basic_skills",
      currentLevel: 0,
      targetLevel: 4,
      urgency: EDUCATION_PRIORITIES.HIGH
    });
  }
  
  return {
    canLearn: score >= 40,
    score,
    priorityAreas,
    learningCapacity: calculateLearningCapacity(experienceYears, skillScore),
    preferredLearningMethods: determinePreferredLearningMethods(experienceYears)
  };
};

/**
 * 🎯 教育価値の計算
 */
export const calculateEducationValue = (staff, availableSurgeries) => {
  let value = 0;
  
  const experienceYears = staff.experienceYears || 0;
  const skillScore = staff.skillScore || 0;
  
  // 若手ほど教育価値が高い
  if (experienceYears <= 2) {
    value += 30;
  } else if (experienceYears <= 4) {
    value += 20;
  } else if (experienceYears <= 6) {
    value += 10;
  }
  
  // スキルスコアが低いほど教育価値が高い
  if (skillScore < 3.0) {
    value += 25;
  } else if (skillScore < 4.5) {
    value += 15;
  } else if (skillScore < 6.0) {
    value += 5;
  }
  
  // 指導能力があれば教育価値が高い
  if (experienceYears >= 3 && skillScore >= 4.0) {
    value += 20;
  }
  
  return value;
};

/**
 * 🔄 補助関数群
 */
const getSkillLevelDescription = (level, role) => {
  const descriptions = {
    1: `${role === "scrub" ? "器械出し" : "外回り"}の見学経験`,
    2: `基本的な${role === "scrub" ? "器械操作" : "外回り業務"}の実践`,
    3: `${role === "scrub" ? "器械出し" : "外回り"}での複数回実践`,
    4: `${role === "scrub" ? "器械準備" : "環境整備"}の習熟`,
    5: `監督下での${role === "scrub" ? "器械出し" : "外回り"}実践`,
    6: `独立した${role === "scrub" ? "器械出し" : "外回り"}業務`,
    7: `${role === "scrub" ? "器械出し" : "外回り"}指導能力`
  };
  return descriptions[level] || "スキル向上";
};

const getEstimatedTimeForLevel = (level) => {
  const times = {
    1: 30,   // 見学
    2: 60,   // 初回実践
    3: 90,   // 複数回実践
    4: 120,  // 習熟
    5: 150,  // 監督下独立
    6: 180,  // 完全独立
    7: 240   // 指導レベル
  };
  return times[level] || 120;
};

const getPrerequisitesForLevel = (level) => {
  const prerequisites = {
    2: ["基本的な手術の流れの理解"],
    3: ["器械の名称と用途の把握"],
    4: ["診療科特有の手技の理解"],
    5: ["安全管理の徹底"],
    6: ["緊急時対応能力"],
    7: ["指導技術の習得"]
  };
  return prerequisites[level] || [];
};

const determinePreferredEducationTypes = (experienceYears, skillScore) => {
  const types = [];
  
  if (experienceYears >= 5 && skillScore >= 5.0) {
    types.push(EDUCATION_TYPES.FINAL_ASSESSMENT, EDUCATION_TYPES.ADDITIONAL_TEACHING);
  } else if (experienceYears >= 3 && skillScore >= 4.0) {
    types.push(EDUCATION_TYPES.SUPERVISED_INDEPENDENCE, EDUCATION_TYPES.SKILL_DEVELOPMENT);
  } else {
    types.push(EDUCATION_TYPES.GUIDED_PRACTICE, EDUCATION_TYPES.ADDITIONAL_OBSERVATION);
  }
  
  return types;
};

const calculateLearningCapacity = (experienceYears, skillScore) => {
  let capacity = 50;
  
  if (experienceYears <= 2) capacity += 30;
  else if (experienceYears <= 4) capacity += 20;
  
  if (skillScore < 4.0) capacity += 20;
  
  return Math.min(100, capacity);
};

const determinePreferredLearningMethods = (experienceYears) => {
  if (experienceYears <= 1) {
    return ["見学中心", "基礎実践"];
  } else if (experienceYears <= 3) {
    return ["指導下実践", "段階的独立"];
  } else {
    return ["自主学習", "専門技術習得"];
  }
};

/**
 * 🕒 時間重複チェック
 */
export const isTimeOverlap = (start1, end1, start2, end2) => {
  const s1 = dayjs(`2024-01-01 ${start1}`);
  const e1 = dayjs(`2024-01-01 ${end1}`);
  const s2 = dayjs(`2024-01-01 ${start2}`);
  const e2 = dayjs(`2024-01-01 ${end2}`);
  
  return s1.isBefore(e2) && s2.isBefore(e1);
};

/**
 * 📊 優先度の数値変換
 */
export const getPriorityValue = (priority) => {
  const values = { 
    [EDUCATION_PRIORITIES.HIGH]: 3, 
    [EDUCATION_PRIORITIES.MEDIUM]: 2, 
    [EDUCATION_PRIORITIES.LOW]: 1 
  };
  return values[priority] || 2;
};